import React, { useState, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import useUser from 'utils/useUser';
import {
  hasAdminPanelAccess,
  hasClinicalRole,
  hasPatientAcceptanceRole,
} from 'utils/accessManager/helpers';
import { isPatientAcceptance } from 'utils/userTypeHelper';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import { CommentType, ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import ChatLeftTextIcon from 'components/atoms/icons/chat-left-text';
import PcctModal from 'components/templates/rpm/patients/patient/generalComments/pcctModal';
import { isCcmModule, isRpmModule } from 'infrastructure/functions';
import BaseForm from 'components/atoms/base-form';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseFormItem from 'components/atoms/base-form/item';
import ModuleTabs from 'components/molecules/module-tabs';
import { useModule } from 'infrastructure/providers/module-provider';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import DraggableModal from 'components/organisms/draggableModal';

import s from './styles.module.scss';

import type { ICreateComment } from 'infrastructure/interfaces';
import type { FC } from 'react';

interface GeneralCommentModalProps {
  toggle: (arg: boolean) => void;
  patientGuid: string;
  isOpen: boolean;
  loading: boolean;
  submit: (data: ICreateComment) => Promise<boolean>;
  comment?: string;
  resetCommentValue?: () => void;
  interactionStatus?: boolean;
  module: ModuleType;
  tabsProps?: {
    showTabs: boolean;
    disableCcm: boolean;
    disableRpm: boolean;
  };
}

const GeneralCommentModalNew: FC<GeneralCommentModalProps> = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  submit,
  comment,
  resetCommentValue,
  interactionStatus,
  module,
  tabsProps,
}) => {
  const { t } = useTranslation();

  const { roleNames, isPractice, isAdmin } = useUser();

  const { updateInteractionStatus } = useUpdateInteractionStatus();

  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });

  const showSwitcher = useMemo(() => {
    return (
      hasPatientAcceptanceRole(roleNames) ||
      hasAdminPanelAccess(roleNames) ||
      hasClinicalRole(roleNames) ||
      isAdmin
    );
  }, [roleNames, isAdmin]);

  const { module: commentType, changeModule: setCommentType } =
    useModule(module);
  const [opened, setOpened] = useState(false);
  const [pcctValues, setPcctValues] = useState({});

  const activeKey = useMemo(() => {
    if (isRpmModule(commentType) && tabsProps?.disableRpm) {
      return ModuleType.CCM;
    }
    if (isCcmModule(commentType) && tabsProps?.disableCcm) {
      return ModuleType.RPM;
    }
    return commentType;
  }, [commentType, tabsProps]);

  const handleSubmit = async (values: { comment: string }) => {
    const postData = {
      patientGuid,
      type: CommentType.Text,
      message: values.comment,
      commentType,
    };
    let interactionConfirm = false;

    if (!isPractice && !isPatientAcceptance(roleNames)) {
      if (isRpmModule(commentType) && !interactionStatus) {
        interactionConfirm = await showMonthlyInteractionAlert();
      }
    }
    toggle(false);
    const response = await submit(postData);

    if (response && interactionConfirm) {
      updateInteractionStatus({
        patientGuid,
        isInteracted: true,
      });
    }

    if (pcctValues) {
      setPcctValues({});
    }
    resetCommentValue?.();
  };

  const formik = useFormik({
    initialValues: { comment: comment ?? '' },
    enableReinitialize: true,
    onSubmit: async (val) => {
      await handleSubmit(val);
      formik.resetForm();
    },
    validateOnChange: false,
  });

  const { submitForm, resetForm, setFieldValue, dirty } = formik;

  const onCancel = () => {
    toggle(false);
    resetForm();
  };

  useEffect(() => {
    if (isRpmModule(module)) setCommentType(ModuleType.RPM);
    if (isCcmModule(module)) setCommentType(ModuleType.CCM);
  }, [module]);

  useEffect(() => {
    if (tabsProps?.disableRpm) setCommentType(ModuleType.CCM);
    if (tabsProps?.disableCcm) setCommentType(ModuleType.RPM);
  }, [tabsProps?.disableCcm, tabsProps?.disableRpm]);

  const showPcctBtn = false;
  const showPcctModal = !isCcmModule(module) && opened;

  return (
    <>
      {isOpen && (
        <DraggableModal
          title={t('labels.generalComment')}
          isOpen={isOpen}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          footer={
            <>
              <BaseButton
                label={t('controls.cancel')}
                type="secondary"
                onClick={onCancel}
                key={t('controls.cancel')}
                loading={loading}
              />
              <BaseButton
                label={t('controls.send')}
                onClick={() => {
                  submitForm();
                }}
                key={t('controls.send')}
                loading={loading}
                dataCy="send-button"
                disabled={!dirty}
              />
            </>
          }
        >
          <BaseForm formik={formik} className={s.form}>
            <div className={s['tabs-templates']}>
              <div className={s['tabs-wrapper']}>
                {(tabsProps?.showTabs ?? showSwitcher) && (
                  <ModuleTabs
                    activeKey={activeKey}
                    rpmDisabled={tabsProps?.disableRpm}
                    ccmDisabled={tabsProps?.disableCcm}
                    onChange={(tab) => setCommentType(tab.key)}
                  />
                )}
              </div>
              {showPcctBtn && (
                <div className={s['templates-wrapper']}>
                  <p>{t('labels.template')}</p>
                  <div className={s['templates-buttons']}>
                    <BaseButton
                      loading={loading}
                      type="secondary"
                      icon={<ChatLeftTextIcon />}
                      onClick={() => setOpened(true)}
                      className={s['pcct-btn']}
                      dataCy="generate-general-comment-button"
                      title={t('controls.generatePCCT')}
                    />
                  </div>
                </div>
              )}
            </div>
            <BaseFormItem name="comment" required>
              <BaseTextArea
                rows={8}
                placeholder={t('controls.typeYourComment')}
                id="general-comment-textarea"
              />
            </BaseFormItem>
          </BaseForm>
        </DraggableModal>
      )}
      {showPcctModal && (
        <PcctModal
          isOpen={opened}
          closeModal={() => setOpened(false)}
          values={pcctValues}
          setValues={setPcctValues}
          setComment={(v) => setFieldValue('comment', v)}
        />
      )}
      {MonthlyInteractionAlert}
    </>
  );
};

export default GeneralCommentModalNew;
